import { useRef, KeyboardEvent, ChangeEvent, useEffect, JSX } from 'react';
import { IGeneralOptions, IInputProps, IMaskedFunc } from '../interfaces';

interface IProps<T> extends IInputProps {
    maskFunc: IMaskedFunc<T>;
    options: T;
}

const BGMaskedInput = <T extends IGeneralOptions>(props: IProps<T>): JSX.Element => {
    const newInputRef = useRef<HTMLInputElement>();
    const widthMachineRef = useRef<HTMLSpanElement>();
    const oldValue = useRef<string>('');
    const oldCaret = useRef<number>(0);

    const keyDownHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
        oldValue.current = event.currentTarget.value;
        oldCaret.current = event.currentTarget.selectionStart;
    };

    const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const input = event.currentTarget;
        const newValue = input.value;
        const newCaret = input.selectionStart;
        let newKey;

        if (oldCaret.current + 1 === newCaret) {
            // new Char inserted
            newKey = newValue.charAt(oldCaret.current);
        } else if (newCaret + 1 === oldCaret.current) {
            newKey = 'Backspace';
        }

        const result = props.maskFunc(oldValue.current, oldCaret.current, newKey, props.options);

        input.value = result.newText;

        setTimeout(() => {
            // setTimeout is here just because of iphone
            input.setSelectionRange(result.newCaretPlace, result.newCaretPlace);
        }, 0);

        if (result.prevent) {
            event.preventDefault();
        }

        props.onChange(result.newText);
    };

    useEffect(() => {
        if (props.options.validRegex) {
            const regex = new RegExp(props.options.validRegex);

            if (!regex.test(props.value)) {
                throw new Error(`Value is not valid, it should match ${props.options.validRegex}`);
            }
        }

        newInputRef.current.value = props.value;
        widthMachineRef.current.innerHTML = props.value;
    }, [props.value]);

    return (
        <label htmlFor="input" className={props.containerClassName}>
            <span className={props.currencySignClassName}>{props.options.currencySign}</span>
            <span style={{ position: 'relative' }}>
                <span style={{ visibility: 'hidden', paddingLeft: '2px' }} ref={widthMachineRef} />
                <input
                    id="input"
                    inputMode="decimal"
                    className={props.inputClassName}
                    ref={newInputRef}
                    onKeyDown={keyDownHandler}
                    onChange={changeHandler}
                />
            </span>
        </label>
    );
};

export default BGMaskedInput;
